import { connectAuthEmulator, type Auth } from 'firebase/auth';
import { connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import type { FirebaseApp } from 'firebase/app';
import { logger } from '@/utils/logger';
import { shouldUseFirebaseEmulators, readFirebaseEnvValue } from '@/utils/firebase/environment';
import { DEFAULT_EMULATOR_HOST, DEFAULT_EMULATOR_PORTS } from '../../../config/emulator-ports.mjs';

interface EmulatorConnections {
  auth?: Auth;
  firestore?: Firestore;
  functions?: Functions;
}

export interface FirebaseEmulatorConfig {
  host: string;
  authPort: number;
  firestorePort: number;
  functionsPort: number;
}

const parsePort = (value: string | undefined, fallback: number): number => {
  if (!value) {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

// `embeddedPort` is the port that was ALREADY part of the configured host string
// (e.g. "https://i9.tail458c6d.ts.net:8443"), and `isSecure` records whether that
// string asked for TLS. Callers need both: staging reaches every emulator through a
// single HTTPS ingress on one port, so appending a per-emulator port would produce
// nonsense like "https://host:8443:8443".
const normalizeHost = (
  host: string
): { protocolHost: string; bareHost: string; embeddedPort?: number; isSecure: boolean } => {
  try {
    const url = host.includes('://') ? new URL(host) : new URL(`http://${host}`);
    const baseHost = url.hostname;
    const portSuffix = url.port ? `:${url.port}` : '';
    return {
      protocolHost: `${url.protocol}//${baseHost}${portSuffix}`,
      bareHost: baseHost,
      embeddedPort: url.port ? Number(url.port) : undefined,
      isSecure: url.protocol === 'https:'
    };
  } catch {
    const sanitized = host.replace(/\/$/, '');
    return {
      protocolHost: `http://${sanitized}`,
      bareHost: sanitized,
      isSecure: false
    };
  }
};

/**
 * The origin for one emulator: the configured host, plus `fallbackPort` ONLY when the
 * host does not already carry a port. Local dev configures a bare host ("127.0.0.1")
 * and gets "http://127.0.0.1:<fallbackPort>" exactly as before.
 */
const originForEmulator = (host: string, fallbackPort: number): string => {
  const { protocolHost, embeddedPort } = normalizeHost(host);
  return embeddedPort ? protocolHost : `${protocolHost}:${fallbackPort}`;
};

let cachedConfig: FirebaseEmulatorConfig | null = null;

const getEmulatorConfig = (): FirebaseEmulatorConfig => {
  if (cachedConfig) {
    return cachedConfig;
  }

  const host = (readFirebaseEnvValue('VITE_FIREBASE_EMULATOR_HOST') || DEFAULT_EMULATOR_HOST).trim();
  cachedConfig = {
    host,
    authPort: parsePort(readFirebaseEnvValue('VITE_AUTH_EMULATOR_PORT'), DEFAULT_EMULATOR_PORTS.auth),
    firestorePort: parsePort(readFirebaseEnvValue('VITE_FIRESTORE_EMULATOR_PORT'), DEFAULT_EMULATOR_PORTS.firestore),
    functionsPort: parsePort(readFirebaseEnvValue('VITE_FUNCTIONS_EMULATOR_PORT'), DEFAULT_EMULATOR_PORTS.functions)
  };

  return cachedConfig;
};

export const getFirebaseEmulatorConfig = (): FirebaseEmulatorConfig => getEmulatorConfig();

const emulatorState: { auth: boolean; firestore: boolean; functions: boolean } = {
  auth: false,
  firestore: false,
  functions: false
};

const connectedAuthInstances = new WeakSet<Auth>();
const connectedFirestoreInstances = new WeakSet<Firestore>();
const connectedFunctionsInstances = new WeakSet<Functions>();

const connectAuthInstance = (auth: Auth) => {
  if (connectedAuthInstances.has(auth)) {
    return;
  }

  const { host, authPort } = getEmulatorConfig();
  // originForEmulator, not `${protocolHost}:${authPort}` — a host configured as
  // "https://i9.tail458c6d.ts.net:8443" already carries its port, and appending
  // authPort produced "https://host:8443:9399", which the Auth SDK cannot parse.
  connectAuthEmulator(auth, originForEmulator(host, authPort), { disableWarnings: true });
  connectedAuthInstances.add(auth);

  if (!emulatorState.auth) {
    emulatorState.auth = true;
    logger.debug('Firebase', 'Connected to Auth emulator', { host, port: authPort });
  }
};

const connectFirestoreInstance = (firestore: Firestore) => {
  if (connectedFirestoreInstances.has(firestore)) {
    return;
  }

  // TLS INGRESS: connectFirestoreEmulator() hardcodes `ssl: false`, so it can only
  // ever build an http:// URL and is physically unable to reach staging's HTTPS
  // Caddy. That case is handled at construction time instead — see
  // getFirestoreEmulatorSettings() and its use in src/firebase.ts — so there is
  // nothing to do here beyond marking the instance as wired.
  if (usesSecureEmulatorIngress()) {
    connectedFirestoreInstances.add(firestore);
    return;
  }

  const { host, firestorePort } = getEmulatorConfig();
  const { bareHost } = normalizeHost(host);
  connectFirestoreEmulator(firestore, bareHost, firestorePort);
  connectedFirestoreInstances.add(firestore);

  if (!emulatorState.firestore) {
    emulatorState.firestore = true;
    logger.debug('Firebase', 'Connected to Firestore emulator', { host, port: firestorePort });
  }
};

const connectFunctionsInstance = (functions: Functions) => {
  if (connectedFunctionsInstances.has(functions)) {
    return;
  }

  // A custom domain instance already points at staging's callable base URL; calling
  // connectFunctionsEmulator on it would clobber that with host:port and send half
  // the app somewhere else. See getFunctionsRegionOrCustomDomain().
  if (getFunctionsBaseUrl()) {
    connectedFunctionsInstances.add(functions);
    return;
  }

  const { host, functionsPort } = getEmulatorConfig();
  const { bareHost } = normalizeHost(host);
  connectFunctionsEmulator(functions, bareHost, functionsPort);
  connectedFunctionsInstances.add(functions);

  if (!emulatorState.functions) {
    emulatorState.functions = true;
    logger.debug('Firebase', 'Connected to Functions emulator', { host, port: functionsPort });
  }
};

export const connectFirebaseEmulators = ({ auth, firestore, functions }: EmulatorConnections) => {
  if (!shouldUseFirebaseEmulators()) {
    return;
  }

  try {
    if (auth) {
      connectAuthInstance(auth);
    }
  } catch (error) {
    logger.warn('Firebase', 'Auth emulator connection failed, falling back to production', { error });
  }

  try {
    if (firestore) {
      connectFirestoreInstance(firestore);
    }
  } catch (error) {
    logger.warn('Firebase', 'Firestore emulator connection failed, falling back to production', { error });
  }

  try {
    if (functions) {
      connectFunctionsInstance(functions);
    }
  } catch (error) {
    logger.warn('Firebase', 'Functions emulator connection failed, falling back to production', { error });
  }
};

export const ensureFunctionsEmulator = (functions: Functions) => {
  if (!shouldUseFirebaseEmulators()) {
    return;
  }

  try {
    connectFunctionsInstance(functions);
    logger.info('Firebase', 'Ensured Functions emulator connection for instance');
  } catch (error) {
    logger.error('Firebase', 'Failed to connect to Functions emulator', { error });
    throw error;
  }
};

export const getFunctionsEmulatorOrigin = (): string => {
  const baseUrl = getFunctionsBaseUrl();
  if (baseUrl) {
    return baseUrl;
  }

  const { host, functionsPort } = getEmulatorConfig();
  return originForEmulator(host, functionsPort);
};

/**
 * True when the emulator suite is reached over TLS (staging behind Caddy) rather than
 * plain http on localhost.
 */
export const usesSecureEmulatorIngress = (): boolean => {
  if (!shouldUseFirebaseEmulators()) {
    return false;
  }
  return normalizeHost(getEmulatorConfig().host).isSecure;
};

/**
 * Firestore settings to merge into initializeFirestore() in src/firebase.ts.
 *
 * WHY THIS EXISTS: connectFirestoreEmulator() hardcodes `ssl: false`. Staging serves
 * Firestore through an HTTPS ingress on one origin, so the only way to reach it from
 * the browser SDK is to set `host` + `ssl: true` at construction time. Returns an
 * empty object for local http emulators and for production, keeping both byte-for-byte
 * on the code path they use today.
 */
export const getFirestoreEmulatorSettings = (): { host?: string; ssl?: boolean } => {
  if (!usesSecureEmulatorIngress()) {
    return {};
  }

  const { host, firestorePort } = getEmulatorConfig();
  const { bareHost, embeddedPort } = normalizeHost(host);
  return { host: `${bareHost}:${embeddedPort ?? firestorePort}`, ssl: true };
};

/**
 * VITE_FUNCTIONS_BASE_URL — the absolute base a callable is appended to, used when
 * Cloud Functions live behind a custom domain/path rather than at
 * `<host>:<port>/<project>/<region>/<fn>`. Staging's value is
 * "https://i9.tail458c6d.ts.net:8443/staging/api/social-work-test-prep-academy/us-central1".
 * Production sets it to the shared `api` function
 * ("https://us-central1-social-work-test-prep-academy.cloudfunctions.net/api").
 * Unset in local dev and in builds without it (CI, Vercel previews), which call
 * the per-function URLs.
 */
export const getFunctionsBaseUrl = (): string | undefined => {
  const raw = readFirebaseEnvValue('VITE_FUNCTIONS_BASE_URL');
  const trimmed = raw?.trim();
  return trimmed ? trimmed.replace(/\/$/, '') : undefined;
};

/**
 * The second argument for getFunctions(). The SDK treats a value starting with
 * "http" as a CUSTOM DOMAIN (it calls `<base>/<functionName>`) and anything else as a
 * region. Returning undefined yields the SDK default (us-central1).
 *
 * EVERY site that builds a Functions instance must go through createFunctions() below,
 * because getFunctions() memoizes per (app, regionOrCustomDomain): mixing arguments
 * would hand different parts of the app different backends.
 */
export const getFunctionsRegionOrCustomDomain = (): string | undefined => getFunctionsBaseUrl();

/** The ONE way to build a Functions instance. See getFunctionsRegionOrCustomDomain(). */
export const createFunctions = (app?: FirebaseApp): Functions =>
  getFunctions(app, getFunctionsRegionOrCustomDomain());
